import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
import metricsService from '../../services/api/metricsService';
import AuthContext from '../../store/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatIsoWeekRange } from '../../utils/dateUtils';

interface WeekCount {
    [key: string]: number;
}

interface UserMetric {
    userId: string;
    name: string;
    email: string;
    total: number;
    ytd: number;
    weekCounts: WeekCount;
}

interface MetricsData {
    totalSearches: number;
    ytdSearches: number;
    users: UserMetric[];
    weekKeys: string[];
    startDate: string;
}

const MetricsPage = () => {
    const { t } = useTranslation();
    const authContext = useContext(AuthContext);
    const departmentName = authContext.getContext()?.department?.name;
    const [data, setData] = useState<MetricsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await metricsService.getMetrics();
                setData(result);
            } catch (error) {
                console.error('Error fetching metrics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full p-8">
                <div className="text-lg">{t('metrics.loading')}</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex justify-center items-center h-full p-8">
                <div className="text-lg text-red-500">{t('metrics.failedToLoad')}</div>
            </div>
        );
    }

    return (
        <div className="p-8 space-y-8">
            <div className="space-y-1">
                <h1 className="text-3xl font-bold">
                    {departmentName
                        ? t('metrics.departmentTitle', { departmentName })
                        : t('metrics.title')}
                </h1>
                <p className="text-sm text-muted-foreground">
                    {t('metrics.description')}
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('metrics.summary.totalSearches.title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.totalSearches}</div>
                        <p className="text-xs text-muted-foreground">
                            {t('metrics.summary.totalSearches.description')}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('metrics.summary.ytdSearches.title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{data.ytdSearches}</div>
                        <p className="text-xs text-muted-foreground">
                            {t('metrics.summary.ytdSearches.description')}
                        </p>
                    </CardContent>
                </Card>
            </div>

            <Card className="col-span-4">
                <CardHeader>
                    <CardTitle>{t('metrics.table.title')}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        {t('metrics.table.description')}
                    </p>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[200px] sticky left-0 bg-background z-10">{t('metrics.table.headers.user')}</TableHead>
                                    <TableHead className="w-[200px]">{t('metrics.table.headers.email')}</TableHead>
                                    <TableHead className="w-[100px] text-center bg-muted/20 font-bold">{t('metrics.table.headers.total')}</TableHead>
                                    <TableHead className="w-[100px] text-center bg-muted/20 font-bold border-r">{t('metrics.table.headers.thisYear')}</TableHead>
                                    {data.weekKeys.map((week) => (
                                        <TableHead key={week} className="text-center min-w-[80px]">
                                            {t('metrics.table.weekPrefix')}{week.split('-')[1]}
                                            <span className="block whitespace-nowrap text-xs font-normal text-muted-foreground">
                                                {formatIsoWeekRange(
                                                    week,
                                                    i18n.resolvedLanguage ||
                                                        i18n.language
                                                )}
                                            </span>
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.users.map((user) => (
                                    <TableRow key={user.userId}>
                                        <TableCell className="font-medium sticky left-0 bg-background z-10">
                                            {user.name}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell className="text-center font-bold bg-muted/20">{user.total}</TableCell>
                                        <TableCell className="text-center font-bold bg-muted/20 border-r">{user.ytd}</TableCell>
                                        {data.weekKeys.map((week) => (
                                            <TableCell key={week} className="text-center">
                                                {user.weekCounts[week] || 0}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default MetricsPage;
